import api from './api';

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled';

export interface BillingLineItemBreakdownEntry {
  referenceType: 'grn' | 'dispatch';
  referenceNumber: string;
  date: string;
  weightTons?: number;
  sqft?: number;
  pallets?: number;
  days?: number;
}

export interface BillingLineItem {
  id: number;
  invoiceId: number;
  chargeType: string;
  description: string;
  quantity: string;
  unit: string;
  rate: string;
  amount: string;
  referenceType: string | null;
  referenceId: number | null;
  breakdown: BillingLineItemBreakdownEntry[] | null;
  createdAt: string;
}

export interface BillingInvoice {
  id: number;
  tenantId: number;
  clientId: number;
  warehouseId: number;
  invoiceNumber: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  subtotal: string;
  cgstPercent: string;
  cgstAmount: string;
  sgstPercent: string;
  sgstAmount: string;
  roundOff: string;
  totalAmount: string;
  status: InvoiceStatus;
  irnNo: string | null;
  ackNo: string | null;
  ackDate: string | null;
  notes: string | null;
  generatedBy: number;
  paidAt: string | null;
  lineItems: BillingLineItem[];
  client?: {
    id: number;
    companyName: string;
    address?: string;
    city?: string;
    state?: string;
    pinCode?: string;
    gstNumber?: string | null;
    panNumber?: string | null;
  };
  warehouse?: {
    id: number;
    name: string;
    address?: string;
    companyName?: string | null;
    companyGstin?: string | null;
    companyPan?: string | null;
    bankName?: string | null;
    bankAccountNo?: string | null;
    bankIfsc?: string | null;
    bankBranch?: string | null;
    companyTagline?: string | null;
    companyEmail?: string | null;
    companyJurisdiction?: string | null;
  };
  createdAt: string;
  updatedAt: string;
}

export interface GenerateInvoicePayload {
  clientId: number;
  warehouseId: number;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  gstPercent?: number;
  labelQty?: number;
  stickerQty?: number;
  packingUnits?: number;
  coldStoragePallets?: number;
  notes?: string;
}

export const billingService = {
  generateInvoice: async (payload: GenerateInvoicePayload): Promise<BillingInvoice> => {
    const response = await api.post('/billing/generate', payload);
    return (response as any).data;
  },

  getInvoices: async (clientId?: number): Promise<BillingInvoice[]> => {
    const params = clientId ? `?clientId=${clientId}` : '';
    const response = await api.get(`/billing/invoices${params}`);
    return (response as any).data;
  },

  getInvoice: async (id: number): Promise<BillingInvoice> => {
    const response = await api.get(`/billing/invoices/${id}`);
    return (response as any).data;
  },

  updateStatus: async (id: number, status: InvoiceStatus): Promise<BillingInvoice> => {
    const response = await api.patch(`/billing/invoices/${id}/status`, { status });
    return (response as any).data;
  },

  updateEinvoice: async (
    id: number,
    payload: { irnNo?: string; ackNo?: string; ackDate?: string },
  ): Promise<BillingInvoice> => {
    const response = await api.patch(`/billing/invoices/${id}/einvoice`, payload);
    return (response as any).data;
  },

  downloadInvoicePdf: async (id: number): Promise<Blob> => {
    return (await api.get(`/billing/invoices/${id}/pdf`, { responseType: 'blob' })) as any;
  },
};

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
